/**
 * Measurements tab — UX/UI Specification §9.7.
 *
 * Every field is optional. Submit stays disabled until at least one value is
 * entered, and the helper text says why — an instructor who only had time for
 * a waist measurement must be able to save exactly that (PRD §9.2).
 */

import { useMemo, useRef, useState } from 'react';
import {
  buildCharts,
  buildCustomChart,
  customMetricsOf,
  formatDateLong,
  formatValue,
  LIMITS,
  METRICS,
  muscleLimit,
  parseTypedNumber,
  type MeasurementRecord,
} from '@pp/shared';
import { ApiError, api } from '../api/client.js';
import { messageFor } from '../api/error-messages.js';
import type { MeasurementItem, MeasurementListResponse } from '../api/types.js';
import { Chart } from '../components/Chart.js';
import { NumberField, numberFieldError } from '../components/ui/NumberField.js';
import { AdminReauthDialog } from '../components/ui/AdminReauthDialog.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { Button } from '../components/ui/Button.js';
import { useApiData } from '../hooks/useApiData.js';
import { useSession } from '../session/SessionContext.js';
import './MeasurementsTab.css';

interface MeasurementsTabProps {
  studentId: string;
  heightCm: number;
  onChanged: () => void;
}

/** Body order, matching the report table (Doc 6 §10.5). */
const FIELDS = [
  { name: 'weightKg', label: 'Kilo', unit: 'kg', limit: LIMITS.weightKg },
  { name: 'waistCm', label: 'Bel', unit: 'cm', limit: LIMITS.circumferenceCm },
  { name: 'hipCm', label: 'Kalça', unit: 'cm', limit: LIMITS.circumferenceCm },
  { name: 'chestCm', label: 'Göğüs', unit: 'cm', limit: LIMITS.circumferenceCm },
  { name: 'rightArmCm', label: 'Sağ Kol', unit: 'cm', limit: LIMITS.circumferenceCm },
  { name: 'leftArmCm', label: 'Sol Kol', unit: 'cm', limit: LIMITS.circumferenceCm },
  {
    name: 'rightThighCm',
    label: 'Sağ Bacak',
    unit: 'cm',
    limit: LIMITS.circumferenceCm,
  },
  { name: 'leftThighCm', label: 'Sol Bacak', unit: 'cm', limit: LIMITS.circumferenceCm },
  { name: 'bodyFatPct', label: 'Yağ Oranı', unit: '%', limit: LIMITS.percent },
] as const;

type FieldName = (typeof FIELDS)[number]['name'];

/** Exported for OverviewTab, which draws the same charts in miniature. */
export function toRecord(item: MeasurementItem): MeasurementRecord {
  return {
    id: item.id,
    measuredAt: item.measuredAt,
    weightKg: item.weightKg,
    waistCm: item.waistCm,
    hipCm: item.hipCm,
    chestCm: item.chestCm,
    rightArmCm: item.rightArmCm,
    leftArmCm: item.leftArmCm,
    rightThighCm: item.rightThighCm,
    leftThighCm: item.leftThighCm,
    bodyFatPct: item.bodyFatPct,
    muscleValue: item.muscle?.value ?? null,
    muscleUnit: item.muscle?.unit ?? null,
    custom: item.custom ?? [],
  };
}

/** Empty and unparseable both mean "no value" once the field has vetted it. */
function parseNumber(raw: string): number | null {
  const value = parseTypedNumber(raw);
  return typeof value === 'number' ? value : null;
}

/** 71.8 → "71,8": what a Turkish keyboard would have typed into the field. */
function toTyped(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value).replace('.', ',');
}

function todayIso(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Istanbul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

export function MeasurementsTab({
  studentId,
  heightCm,
  onChanged,
}: MeasurementsTabProps) {
  const { data, error, loading, reload } = useApiData<MeasurementListResponse>(
    `/api/students/${studentId}/measurements`,
  );
  const { hasAdminAccess } = useSession();

  const [adding, setAdding] = useState(false);
  /** The measurement being corrected; null when the form is adding a new one. */
  const [editingId, setEditingId] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [measuredAt, setMeasuredAt] = useState(todayIso());
  const [values, setValues] = useState<Record<string, string>>({});
  const [muscleValue, setMuscleValue] = useState('');
  const [muscleUnit, setMuscleUnit] = useState<'percent' | 'kg'>('percent');
  /*
   * Measurements the product does not name. Held as typed text like the rest
   * of the form, and vetted on save.
   */
  const [custom, setCustom] = useState<{ label: string; unit: string; value: string }[]>(
    [],
  );
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string>();
  const [confirmDelete, setConfirmDelete] = useState<MeasurementItem | null>(null);
  const [reauthFor, setReauthFor] = useState<(() => void) | null>(null);

  const items = data?.items ?? [];

  const charts = useMemo(() => {
    const records = items.map(toRecord);
    // The studio's own metrics get a chart like any other row.
    return [
      ...buildCharts(records, heightCm),
      ...customMetricsOf(records)
        .map((metric) => buildCustomChart(records, metric))
        .filter((chart): chart is NonNullable<typeof chart> => chart !== null),
    ];
  }, [items, heightCm]);

  /** Labels this student already has, so one metric is not retyped as two. */
  const knownCustom = useMemo(() => {
    const seen = new Map<string, { label: string; unit: string }>();
    for (const item of items) {
      for (const value of item.custom ?? []) {
        seen.set(value.labelNormalized, { label: value.label, unit: value.unit });
      }
    }
    return [...seen.values()];
  }, [items]);

  const filledCustom = custom.filter(
    (entry) => entry.label.trim() !== '' && parseNumber(entry.value) !== null,
  );

  const hasAnyEntry =
    FIELDS.some((f) => parseNumber(values[f.name] ?? '') !== null) ||
    parseNumber(muscleValue) !== null ||
    filledCustom.length > 0;

  // A value the server would reject should not travel: the instructor sees
  // which field is wrong here, where a rejected POST only says "invalid".
  const outOfRange =
    FIELDS.some((f) => numberFieldError(values[f.name] ?? '', f.limit) !== null) ||
    numberFieldError(muscleValue, muscleLimit(muscleUnit)) !== null;

  function resetForm() {
    setEditingId(null);
    setValues({});
    setCustom([]);
    setMuscleValue('');
    setMeasuredAt(todayIso());
    setFormError(undefined);
  }

  /**
   * Corrects an entry by loading it into the same form that adds one, so the
   * fields, the bounds and the "at least one value" rule are the same in both.
   * Saving replaces the record: a field left empty is a value removed.
   */
  function startEdit(item: MeasurementItem) {
    const next: Record<string, string> = {};
    for (const field of FIELDS) {
      next[field.name] = toTyped(item[field.name]);
    }
    setValues(next);
    setMuscleValue(toTyped(item.muscle?.value));
    setMuscleUnit(item.muscle?.unit ?? 'percent');
    setCustom(
      (item.custom ?? []).map((entry) => ({
        label: entry.label,
        unit: entry.unit,
        value: toTyped(entry.value),
      })),
    );
    setMeasuredAt(item.measuredAt);
    setFormError(undefined);
    setEditingId(item.id);
    setAdding(true);
    // The form sits above the list; without this the tap seems to do nothing.
    requestAnimationFrame(() =>
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    );
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setFormError(undefined);

    const payload: Record<string, unknown> = { measuredAt };
    for (const field of FIELDS) {
      payload[field.name] = parseNumber(values[field.name] ?? '');
    }
    const muscle = parseNumber(muscleValue);
    payload.muscleValue = muscle;
    payload.muscleUnit = muscle === null ? null : muscleUnit;
    payload.custom = filledCustom.map((entry) => ({
      label: entry.label.trim(),
      unit: entry.unit.trim() || 'cm',
      value: parseNumber(entry.value),
    }));

    try {
      if (editingId) {
        await api.patch(`/api/students/${studentId}/measurements/${editingId}`, payload);
      } else {
        await api.post(`/api/students/${studentId}/measurements`, payload);
      }
      resetForm();
      setAdding(false);
      reload();
      onChanged();
    } catch (err) {
      // The form keeps everything the user typed.
      setFormError(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
    } finally {
      setBusy(false);
    }
  }

  /**
   * Deleting a measurement is not reversible, so it asks first. There is no
   * undo to offer: the row and its history are gone.
   */
  async function remove(item: MeasurementItem) {
    try {
      await api.delete(`/api/students/${studentId}/measurements/${item.id}`);
      reload();
      onChanged();
    } catch (err) {
      setFormError(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
    }
  }

  /** Runs now if the admin window is open, otherwise buys ten minutes first. */
  function withAdmin(action: () => void) {
    if (hasAdminAccess()) action();
    else setReauthFor(() => action);
  }

  return (
    <div className="meas">
      <div className="meas__head">
        <h2 className="profile__section">Ölçümler</h2>
        {!adding && (
          <Button type="button" onClick={() => setAdding(true)}>
            Ölçüm Ekle
          </Button>
        )}
      </div>

      {error && <p role="alert">{error}</p>}

      {adding && (
        <form onSubmit={submit} className="meas__form" noValidate ref={formRef}>
          {editingId && (
            <div className="meas__editing">
              <h3>Ölçümü Düzenle</h3>
              <p>
                Kaydettiğinizde bu ölçümün değerleri güncellenir. Daha önce oluşturulmuş
                raporlar değişmez.
              </p>
            </div>
          )}

          <label className="meas__date">
            <span>Ölçüm Tarihi</span>
            <input
              type="date"
              value={measuredAt}
              max={todayIso()}
              onChange={(e) => setMeasuredAt(e.target.value)}
              required
            />
          </label>

          <div className="meas__grid">
            {FIELDS.map((field) => (
              <NumberField
                key={field.name}
                className="meas__field"
                label={
                  <>
                    {field.label} <em>({field.unit})</em>
                  </>
                }
                limit={field.limit}
                value={values[field.name] ?? ''}
                onChange={(raw) =>
                  setValues((v) => ({ ...v, [field.name as FieldName]: raw }))
                }
              />
            ))}

            {/* The unit travels with the value (D-15), and it also decides the
                bound: 60 % and 60 kg are both plausible, 111 % is not. */}
            <NumberField
              className="meas__field"
              label={
                <>
                  Kas <em>(değer)</em>
                </>
              }
              limit={muscleLimit(muscleUnit)}
              value={muscleValue}
              onChange={setMuscleValue}
              suffix={
                <select
                  className="meas__muscle-unit"
                  value={muscleUnit}
                  onChange={(e) => setMuscleUnit(e.target.value as 'percent' | 'kg')}
                  aria-label="Kas birimi"
                >
                  <option value="percent">%</option>
                  <option value="kg">kg</option>
                </select>
              }
            />
          </div>

          {/*
           * Measurements the product does not name. A studio that tracks a
           * wrist should not have to wait for a release; the label and the
           * unit are the instructor's own words.
           */}
          <div className="meas__custom">
            {custom.map((entry, index) => (
              <div className="meas__custom-row" key={index}>
                <label className="meas__custom-label">
                  <span>Ölçüm adı</span>
                  <input
                    type="text"
                    list="pp-known-metrics"
                    placeholder="El Bileği"
                    value={entry.label}
                    onChange={(e) => {
                      const label = e.target.value;
                      // Reusing a label the student already has brings its
                      // unit with it, so one metric keeps one unit.
                      const known = knownCustom.find(
                        (item) =>
                          item.label.toLocaleLowerCase('tr-TR') ===
                          label.toLocaleLowerCase('tr-TR'),
                      );
                      setCustom((list) =>
                        list.map((item, i) =>
                          i === index
                            ? { ...item, label, unit: known?.unit ?? item.unit }
                            : item,
                        ),
                      );
                    }}
                  />
                </label>

                <NumberField
                  className="meas__custom-value"
                  label="Değer"
                  limit={LIMITS.goalValue}
                  value={entry.value}
                  onChange={(raw) =>
                    setCustom((list) =>
                      list.map((item, i) =>
                        i === index ? { ...item, value: raw } : item,
                      ),
                    )
                  }
                  suffix={
                    <input
                      type="text"
                      className="meas__custom-unit"
                      aria-label="Birim"
                      placeholder="cm"
                      maxLength={12}
                      value={entry.unit}
                      onChange={(e) =>
                        setCustom((list) =>
                          list.map((item, i) =>
                            i === index ? { ...item, unit: e.target.value } : item,
                          ),
                        )
                      }
                    />
                  }
                />

                <button
                  type="button"
                  className="meas__custom-remove"
                  onClick={() => setCustom((list) => list.filter((_, i) => i !== index))}
                  aria-label="Bu ölçümü kaldır"
                >
                  Kaldır
                </button>
              </div>
            ))}

            <datalist id="pp-known-metrics">
              {knownCustom.map((item) => (
                <option key={item.label} value={item.label} />
              ))}
            </datalist>

            <button
              type="button"
              className="meas__custom-add"
              onClick={() =>
                setCustom((list) => [...list, { label: '', unit: 'cm', value: '' }])
              }
            >
              + Diğer ölçüm ekle
            </button>
          </div>

          {formError && (
            <p className="meas__error" role="alert">
              {formError}
            </p>
          )}

          <div className="meas__actions">
            <p className="meas__hint">
              {hasAnyEntry
                ? 'Boş bıraktığınız alanlar kaydedilmez.'
                : 'Kaydetmek için en az bir değer girin.'}
            </p>
            <div className="meas__buttons">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setAdding(false);
                  resetForm();
                }}
              >
                Vazgeç
              </Button>
              <Button type="submit" loading={busy} disabled={!hasAnyEntry || outOfRange}>
                Kaydet
              </Button>
            </div>
          </div>
        </form>
      )}

      {charts.length > 0 && (
        <div className="meas__charts">
          {charts.map((chart) => (
            <Chart key={chart.metricKey} chart={chart} />
          ))}
        </div>
      )}

      {loading && <p className="profile__muted">Yükleniyor…</p>}

      {!loading && items.length === 0 && !adding && (
        <div className="profile__empty">
          <p>Bu öğrenci için henüz ölçüm girilmemiş.</p>
          <Button type="button" onClick={() => setAdding(true)}>
            Ölçüm Ekle
          </Button>
        </div>
      )}

      {items.length > 0 && (
        <ul className="meas__list">
          {items.map((item) => {
            const record = toRecord(item);
            const entries = FIELDS.filter(
              (f) => record[f.name as keyof MeasurementRecord] !== null,
            );
            return (
              <li
                key={item.id}
                className={
                  item.id === editingId ? 'meas__row meas__row--editing' : 'meas__row'
                }
              >
                <div className="meas__row-head">
                  <span className="meas__row-date">
                    {formatDateLong(item.measuredAt)}
                  </span>
                  <div className="meas__row-actions">
                    <button
                      type="button"
                      className="meas__edit"
                      onClick={() => startEdit(item)}
                      aria-label={`${formatDateLong(item.measuredAt)} tarihli ölçümü düzenle`}
                    >
                      Düzenle
                    </button>
                    <button
                      type="button"
                      className="meas__delete"
                      onClick={() => setConfirmDelete(item)}
                    >
                      Sil
                    </button>
                  </div>
                </div>
                <div className="meas__values">
                  {entries.map((field) => (
                    <span key={field.name} className="meas__value">
                      <em>{field.label}</em>{' '}
                      {formatValue(
                        record[field.name as keyof MeasurementRecord] as number,
                        field.unit,
                        METRICS.weight.decimals,
                      )}
                    </span>
                  ))}
                  {item.muscle && (
                    <span className="meas__value">
                      <em>Kas</em>{' '}
                      {formatValue(
                        item.muscle.value,
                        item.muscle.unit === 'percent' ? '%' : 'kg',
                        1,
                      )}
                    </span>
                  )}
                  {/* The studio's own fields — a visit that recorded only one
                      of these (no fixed metric at all) would otherwise show
                      nothing but its date. */}
                  {(item.custom ?? []).map((value) => (
                    <span key={value.labelNormalized} className="meas__value">
                      <em>{value.label}</em> {formatValue(value.value, value.unit, 1)}
                    </span>
                  ))}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Ölçüm silinsin mi?"
          body={`${formatDateLong(confirmDelete.measuredAt)} tarihli ölçüm kalıcı olarak silinecek. Bu işlem geri alınamaz.`}
          confirmLabel="Sil"
          destructive
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => {
            const item = confirmDelete;
            setConfirmDelete(null);
            withAdmin(() => void remove(item));
          }}
        />
      )}

      {reauthFor && (
        <AdminReauthDialog
          action="Ölçümü kalıcı olarak silmek"
          onCancel={() => setReauthFor(null)}
          onConfirmed={() => {
            const action = reauthFor;
            setReauthFor(null);
            action();
          }}
        />
      )}
    </div>
  );
}
